import * as localforage from 'localforage'
import type { FieldWorkApplicationName } from '@/types/global/releaseNotes.ts'
import router from '@/router'
import { ENV } from '@/settingsFieldwork.ts'
import { getContentfulEntry } from '@/helpers/contentful.ts'
import CryptoJS from 'crypto-js'
import type { GlobalAlertType } from '@/types/global/globalAlert.ts'

const CONTENTFUL_SPACE_ID = 'htxgz60k3z04'

export function dismissErrorId (errorId: string) {
  const ignoredErrorIds = localforage.getItem<string[]>('dismissedErrorIds')
  return ignoredErrorIds.then((ids) => {
    if (ids === null) {
      localforage.setItem('dismissedErrorIds', [errorId])
      return [errorId]
    } else {
      const uniqueIds = new Set([...ids, errorId])
      const uniqueIdArray = Array.from(uniqueIds)
      localforage.setItem('dismissedErrorIds', uniqueIdArray)
      return ids
    }
  })
}

export async function getDismissedErrorIds (): Promise<string[]> {
  const ids = await localforage.getItem<string[]>('dismissedErrorIds')
  return ids || []
}

export function clearDismissedErrorIds (): void {
  localforage.removeItem('dismissedErrorIds')
}

// -------------------------------------------------------------
// Contentful-driven global alert banner
// -------------------------------------------------------------

export async function dismissGlobalAlertByHash (hash: string) {
  const ignoredAlertHashes = localforage.getItem<string[]>('dismissedAlertHashes')
  return ignoredAlertHashes.then((hashes) => {
    if (hashes === null) {
      localforage.setItem('dismissedAlertHashes', [hash])
      return [hash]
    } else {
      const uniqueHashes = new Set([...hashes, hash])
      const uniqueHashArray = Array.from(uniqueHashes)
      localforage.setItem('dismissedAlertHashes', uniqueHashArray)
      return uniqueHashArray
    }
  })
}

export async function getDismissedAlertHashes (): Promise<string[]> {
  const hashes = await localforage.getItem<string[]>('dismissedAlertHashes')
  return hashes || []
}

export function clearDismissedAlertHashes (): void {
  localforage.removeItem('dismissedAlertHashes')
}

/**
 * Get the contentful record id for the global alert banner
 */
function getContentfulRecordId (): string {
  const appName = getCurrentAppName()
  switch (appName) {
    case 'Capacity Manager':
      return '2gdzNbsQZhRTAWMRGvOPDP'

    case 'Fault Management':
      return '1VWBdPKOb1D120HWGLjXGU'

    case 'Schedule Manager':
      return '1Imo3tsqwYRYtPQUAcs8hL'

    case 'Network Recovery Manager':
      return 'OijdXtmiSciXzxrszxoJy'

    case 'PLANT Manager':
      return '5oUF1gZpsgwFci5cZGNOVs'

    default:
      // Field Work
      return 'eJlx0wcZzhkg4NrEph32c'
  }
}

function getCurrentAppName (): FieldWorkApplicationName {
  // use theme name from router to identify the current app
  const theme = router.currentRoute.value.meta.theme as string
  switch (theme) {
    case 'CM':
      return 'Capacity Manager'
    case 'FMP':
      return 'Fault Management'
    case 'SM':
      return 'Schedule Manager'
    case 'NRM':
      return 'Network Recovery Manager'
    case 'PLANT':
      return 'PLANT Manager'
    default:
      return 'Field Work'
  }
}

function getContenfulEnvironment (): string {
  return ENV === 'PROD' ? 'master' : 'DEV'
}

function getContentfulAccessToken (): string {
  if (ENV === 'PROD') {
    // Content Delivery API
    return 'EjxQpVQ1IpLvMr_47YOM66OjCpk7TQQqFWNJJfLG-hI'
  } else {
    // Preview API
    return '0H4sd9N81dMtvnueI9-ykufzc75wg0rZnQJZ0kiw6PY'
  }
}

function usePreviewApi (): boolean {
  return ENV !== 'PROD'
}

function hashString (input: string): string {
  return CryptoJS.SHA256(input).toString()
}

function hashOfAlertRecord (title: string, copy: string): string {
  const str = `${title}:${copy}`
  return hashString(str)
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
function extractCTA (alertFields: any) {
  if (alertFields.alertCta) {
    const ctaFields = alertFields.alertCta.fields
    const linkFields = ctaFields.ctaLink?.fields

    if (linkFields) {
      return {
        url: linkFields.externalUrl,
        label: linkFields.linkText
      }
    }
  }
  return undefined
}

export function getGlobalAlertRecord () {
  const entryId = getContentfulRecordId()
  if (entryId !== '') {
    return getContentfulEntry({
      entryId,
      spaceId: CONTENTFUL_SPACE_ID,
      accessToken: getContentfulAccessToken(),
      environment: getContenfulEnvironment(),
      preview: usePreviewApi()
    }).then((response) => {
      const alertFields = response.fields

      // Check if it's enabled on contentful
      if (alertFields.enabled === true) {
        const alertCopy = alertFields.alertCopy as string
        const alertTitle = alertFields.alertHeading as string
        const hash = hashOfAlertRecord(alertTitle, alertCopy)

        const entry: GlobalAlertType = {
          title: alertTitle,
          copy: alertCopy,
          hash,
          application: getCurrentAppName(),
          cta: extractCTA(alertFields)
        }
        return entry
      } else {
        return null
      }
    }).catch(() => {
      return null
    })
  } else {
    return Promise.resolve(null)
  }
}
